/**
 * KAN-845: карточка шага экрана "Начало работы": номер или галочка,
 * заголовок, пометка "можно позже", содержимое шага.
 * KAN-873: highlighted - экран открыт к этому шагу из строки пометок:
 * карточка подсвечена и прокручена в видимую область.
 */
import { useRef, type ReactNode } from 'react';
import { useScrollIntoViewOnce } from '@/hooks/useScrollIntoViewOnce';
import { Check } from 'lucide-react';
import { ONBOARDING_TEXTS, type OnboardingStepId } from '@/lib/managerOnboarding';
import { cn } from '@/lib/utils';

interface Props {
  id: OnboardingStepId;
  index: number;
  title: string;
  done: boolean;
  optional?: boolean;
  highlighted?: boolean;
  children?: ReactNode;
}

export function OnboardingStepCard({
  id,
  index,
  title,
  done,
  optional = false,
  highlighted = false,
  children,
}: Props): JSX.Element {
  const ref = useRef<HTMLLIElement>(null);
  useScrollIntoViewOnce(ref, highlighted);
  return (
    <li
      ref={ref}
      className={cn(
        'scroll-mt-4 rounded-2xl border bg-white p-4',
        done ? 'border-green-200' : 'border-gray-200',
        highlighted && 'border-amber-300 ring-2 ring-amber-300'
      )}
      data-testid={`onboarding-step-${id}`}
      data-done={done ? 'true' : 'false'}
      data-highlighted={highlighted ? 'true' : 'false'}
    >
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-base font-semibold',
            done ? 'bg-green-600 text-white' : 'bg-blue-50 text-blue-700'
          )}
          aria-hidden="true"
        >
          {done ? <Check className="h-5 w-5" /> : index}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold leading-snug text-gray-900">{title}</h2>
          {done ? (
            <p className="text-sm text-green-700">{ONBOARDING_TEXTS.done}</p>
          ) : (
            optional && <p className="text-sm text-gray-500">{ONBOARDING_TEXTS.optional}</p>
          )}
        </div>
      </div>
      {children && <div className="mt-3 space-y-3">{children}</div>}
    </li>
  );
}
